import React from 'react';
import { GlassCard } from '../shared/GlassCard.js';
import {
  IconReferralCopy,
  IconReferralDirect,
  IconReferralIncome,
  IconReferralUpline,
} from './ReferralDataIcons';

function ReferralStatCard({ icon, title, children, loading }) {
  return (
    <GlassCard variant="gold" hover className="p-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-hash-gold/30 via-hash-gold/10 to-medora-blue/15 border border-hash-gold/35">
          {icon}
        </div>
        <p className="text-sm font-medium text-gray-300 leading-tight">{title}</p>
      </div>
      {loading ? (
        <div className="py-2 text-center text-gray-500">…</div>
      ) : (
        children
      )}
    </GlassCard>
  );
}

function ReferralStatsGrid({
  allIncome,
  partnerLengths,
  uplineAddress,
  uplineUser,
  refTopAddr,
  refTopUser,
  onCopyAddress,
  referralFullLink,
  onCopyReferralLink,
  loading,
}) {
  const totalReferrals = Number(partnerLengths?.[0]) || 0;
  const displayAddress = uplineAddress || refTopAddr || '';
  const displayUser = uplineUser || refTopUser || '';
  const hasUpline = Boolean(displayUser || displayAddress);
  const avatarInitial = displayUser
    ? displayUser.trim().charAt(0).toUpperCase()
    : displayAddress
      ? (displayAddress.slice(2, 3) || '?').toUpperCase()
      : '?';
  const incomeDisplay =
    typeof allIncome === 'number' && Number.isFinite(allIncome)
      ? allIncome.toLocaleString(undefined, { maximumFractionDigits: 6 })
      : '0';

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8">
      <ReferralStatCard
        loading={loading}
        title="All Affiliate Income"
        icon={<IconReferralIncome className="w-4 h-4 text-hash-gold" />}
      >
        <p className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold tabular-nums leading-none">
          {incomeDisplay}
        </p>
        <p className="text-gray-600 text-xs mt-1">USDT earned from affiliates</p>
      </ReferralStatCard>

      <ReferralStatCard
        loading={loading}
        title="Your affiliate link"
        icon={<IconReferralCopy className="w-4 h-4 text-hash-gold" />}
      >
        {referralFullLink ? (
          <button
            type="button"
            onClick={onCopyReferralLink}
            className="group w-full text-left rounded-lg bg-dark-secondary/70 border border-hash-gold/15 px-2.5 py-2 hover:border-hash-gold/40 hover:bg-dark-secondary/90 transition-colors cursor-pointer"
            title={referralFullLink}
          >
            <p className="text-[11px] font-mono text-gray-300 group-hover:text-hash-gold break-all leading-snug line-clamp-2 transition-colors">
              {referralFullLink}
            </p>
            <p className="text-[10px] text-gray-600 mt-1">Click to copy</p>
          </button>
        ) : (
          <div className="space-y-0.5">
            <p className="text-sm text-gray-400">No share link yet</p>
            <p className="text-xs text-gray-600">Register an affiliate code to generate your link.</p>
          </div>
        )}
      </ReferralStatCard>

      <ReferralStatCard
        loading={loading}
        title="Direct Affiliates"
        icon={<IconReferralDirect className="w-4 h-4 text-hash-gold" />}
      >
        <p className="text-xl sm:text-2xl font-bold text-hash-gold text-shadow-gold tabular-nums leading-none">
          {totalReferrals}
        </p>
        <p className="text-gray-600 text-xs mt-1">Partners on your direct level</p>
      </ReferralStatCard>

      <ReferralStatCard
        loading={loading}
        title="Your Upline User"
        icon={<IconReferralUpline className="w-4 h-4 text-hash-gold" />}
      >
        {hasUpline ? (
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-hash-gold-dark bg-gradient-to-br from-hash-gold/35 via-hash-gold/15 to-medora-blue/20 border border-hash-gold/40"
              aria-hidden
            >
              {avatarInitial}
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 text-sm leading-tight">
              <span className="font-bold text-hash-gold truncate" title={displayUser || undefined}>
                {displayUser || '—'}
              </span>
            
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 py-0.5">
            <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center bg-white/5 border border-white/10 text-gray-500 text-sm font-semibold">
              ?
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm text-gray-400 leading-tight">No upline assigned</p>
              <p className="text-xs text-gray-600 leading-tight">Sponsor appears once linked.</p>
            </div>
          </div>
        )}
      </ReferralStatCard>
    </div>
  );
}

export default ReferralStatsGrid;
